import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  TimeframeInput,
  useGetOrganisationalHeroMetricsQuery,
} from 'src/graphql';
import { InsightsDashboard, State } from 'src/types';
import { convertMetricNameToId, getTestIdFromString } from 'src/utils';
import { buildLocationQueryVariables } from 'src/utils/insights/buildLocationQueryVariables';

interface Parameters {
  globalFilterSites: string[];
  globalFilterCountries: string[];
  selectedCaseCountryCodes: string[];
  selectedCaseRegionIds: string[];
  globalFilterTimeframe: TimeframeInput;
}

export const useHeroMetrics = ({
  globalFilterSites,
  globalFilterCountries,
  selectedCaseCountryCodes,
  selectedCaseRegionIds,
  globalFilterTimeframe,
}: Parameters) => {
  const storedGlobalFilters = useSelector(
    (state: State) =>
      state.insightsGlobalFilters[InsightsDashboard.ORGANISATIONAL],
  );
  const currentGlobalLocationFilter =
    storedGlobalFilters.currentGlobalLocationFilter;
  const [timeframe, setTimeframe] = useState(globalFilterTimeframe);
  const [selectedSites, setSelectedSites] = useState(globalFilterSites);
  const [selectedCountries, setSelectedCountries] = useState(
    globalFilterCountries,
  );
  const [selectedCaseCountries, setSelectedCaseCountries] = useState(
    selectedCaseCountryCodes,
  );
  const [selectedCaseRegions, setSelectedCaseRegions] = useState(
    selectedCaseRegionIds,
  );
  const queryVariables = buildLocationQueryVariables({
    locationFilter: currentGlobalLocationFilter,
    locationFilterCategory: storedGlobalFilters.locationFilterCategory,
    sites: selectedSites,
    countries: selectedCountries,
    caseCountryCodes: selectedCaseCountries,
    caseRegionIds: selectedCaseRegions,
  });
  const { data, isLoading } = useGetOrganisationalHeroMetricsQuery({
    timeframe,
    ...queryVariables,
  });

  useEffect(() => {
    setSelectedSites(globalFilterSites);
  }, [globalFilterSites]);

  useEffect(() => {
    setSelectedCountries(globalFilterCountries);
  }, [globalFilterCountries]);

  useEffect(() => {
    setSelectedCaseCountries(selectedCaseCountryCodes);
  }, [selectedCaseCountryCodes]);

  useEffect(() => {
    setSelectedCaseRegions(selectedCaseRegionIds);
  }, [selectedCaseRegionIds]);

  useEffect(() => {
    setTimeframe(globalFilterTimeframe);
  }, [globalFilterTimeframe]);

  const totalCases =
    data?.getTotalCasesHeroMetric.subCategory?.find(
      (cat) => cat.category === 'All cases',
    )?.count ?? 0;

  const heroMetricData = [
    data?.getTotalCasesHeroMetric,
    data?.getLocationWithMostCasesHeroMetric,
    data?.getDepartmentWithMostCasesHeroMetric,
    data?.getCategoryWithMostReportsHeroMetric,
    data?.getMonthWithMostReportsHeroMetric,
  ];
  return {
    data: heroMetricData.filter(Boolean).map((metric) => ({
      id: convertMetricNameToId(metric?.category ?? ''),
      title: metric?.category ?? '',
      testId: getTestIdFromString(metric?.category ?? ''),
      metrics:
        metric?.subCategory?.map((s) => {
          if (s.category === 'Last 7 days' && totalCases) {
            return {
              title: s.category,
              value: s.count,
            };
          }
          return {
            title: s.category ?? '',
            value: s.count === 0 ? '—' : s.count,
          };
        }) ?? [],
      tooltip: metric?.tooltip ?? '',
      descriptor: metric?.descriptor ?? '',
    })),
    isLoading,
  };
};
